/* ============================================================
   Christian Catalogue — cart layer
   Owns the drawer and all cart mutations. Exposes window.CXCart
   so the product and showcase scripts can open/refresh it.

   Every control here has a no-JS counterpart: quantity inputs are
   named updates[] inside a form posting to /cart, and checkout is a
   real submit button. If this file fails to load, the cart still works.
   ============================================================ */
(function () {
  'use strict';

  var DRAWER_SECTION = 'cx-cart-drawer';
  var PAGE_SECTION = 'cx-cart-page';

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function drawerEl() {
    return document.querySelector('[data-cx-drawer]');
  }

  /* ---------- open / close ---------- */
  var lastFocus = null;

  function openDrawer() {
    var d = drawerEl();
    if (!d) {
      window.location.href = root() + 'cart';
      return;
    }
    lastFocus = document.activeElement;
    d.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    var close = d.querySelector('[data-cx-drawer-close]');
    if (close && close.focus) close.focus();
  }

  function closeDrawer() {
    var d = drawerEl();
    if (!d) return;
    d.classList.remove('is-open');
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function busy(on) {
    var d = drawerEl();
    if (d) d.classList.toggle('is-busy', !!on);
  }

  function message(text, state) {
    document.querySelectorAll('[data-cx-cart-msg]').forEach(function (el) {
      el.textContent = text || '';
      if (state) el.dataset.state = state;
      else delete el.dataset.state;
    });
  }

  /* ---------- re-render from the server ---------- */
  function wanted() {
    var list = [];
    if (drawerEl()) list.push(DRAWER_SECTION);
    if (document.querySelector('[data-cx-cartpage]')) list.push(PAGE_SECTION);
    if (document.getElementById('cart-icon-bubble')) list.push('cart-icon-bubble');
    return list;
  }

  function swap(html, selector, target) {
    var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (fresh && target) target.innerHTML = fresh.innerHTML;
  }

  function refresh(reopen) {
    var list = wanted();
    if (!list.length) return Promise.resolve();

    return fetch(root() + '?sections=' + list.join(','))
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var wasOpen = reopen || (drawerEl() && drawerEl().classList.contains('is-open'));

        if (data[DRAWER_SECTION]) {
          var d = drawerEl();
          swap(data[DRAWER_SECTION], '[data-cx-drawer]', d);
          if (wasOpen && d) {
            d.classList.add('is-open');
            document.body.style.overflow = 'hidden';
          }
        }
        if (data[PAGE_SECTION]) {
          swap(data[PAGE_SECTION], '[data-cx-cartpage]', document.querySelector('[data-cx-cartpage]'));
        }
        if (data['cart-icon-bubble']) {
          swap(data['cart-icon-bubble'], '#cart-icon-bubble', document.getElementById('cart-icon-bubble'));
        }
        bind(document);
      })
      .catch(function () {
        // If the re-render fails the cart is still correct server-side.
        message('Cart updated — refresh to see the latest.', 'error');
      });
  }

  /* ---------- mutations ---------- */
  function changeLine(key, quantity) {
    busy(true);
    message('Updating…');

    return fetch(root() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('change');
        return r.json();
      })
      .then(function () {
        message('');
        return refresh(true);
      })
      .catch(function () {
        message("That couldn't be updated. Try again.", 'error');
      })
      .then(function () { busy(false); });
  }

  function addVariant(id, quantity) {
    busy(true);
    message('Adding…');

    return fetch(root() + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(id), quantity: quantity || 1 }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add');
        return r.json();
      })
      .then(function () {
        message('');
        return refresh(true).then(openDrawer);
      })
      .catch(function () {
        message("That couldn't be added. Try again.", 'error');
      })
      .then(function () { busy(false); });
  }

  /* ---------- wiring ---------- */
  function bind(scope) {
    scope = scope || document;

    // Header cart icon opens the drawer instead of navigating.
    document.querySelectorAll('#cart-icon-bubble, a[href$="/cart"]').forEach(function (link) {
      if (link.dataset.cxBound) return;
      link.dataset.cxBound = '1';
      link.addEventListener('click', function (e) {
        if (!drawerEl()) return; // no drawer, let the link work
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        openDrawer();
      });
    });

    scope.querySelectorAll('[data-cx-drawer-close]').forEach(function (b) {
      if (b.dataset.cxBound) return;
      b.dataset.cxBound = '1';
      b.addEventListener('click', closeDrawer);
    });

    // Quantity steppers and direct entry.
    scope.querySelectorAll('[data-cx-line]').forEach(function (line) {
      if (line.dataset.cxBound) return;
      line.dataset.cxBound = '1';

      var key = line.dataset.cxKey;
      var input = line.querySelector('[data-cx-qty]');

      line.querySelectorAll('[data-cx-step]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var next = Math.max(0, parseInt(input.value, 10) + parseInt(btn.dataset.cxStep, 10));
          input.value = next;
          changeLine(key, next);
        });
      });

      if (input) {
        input.addEventListener('change', function () {
          var n = parseInt(input.value, 10);
          if (isNaN(n) || n < 0) n = 0;
          changeLine(key, n);
        });
      }

      var remove = line.querySelector('[data-cx-remove]');
      if (remove) {
        remove.addEventListener('click', function () { changeLine(key, 0); });
      }
    });

    scope.querySelectorAll('[data-cx-upsell-add]').forEach(function (btn) {
      if (btn.dataset.cxBound) return;
      btn.dataset.cxBound = '1';
      btn.addEventListener('click', function () {
        addVariant(btn.dataset.cxUpsellAdd, 1);
      });
    });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    var d = drawerEl();
    if (d && d.classList.contains('is-open')) closeDrawer();
  });

  // Keep focus inside the drawer while it is open.
  document.addEventListener('focusin', function (e) {
    var d = drawerEl();
    if (!d || !d.classList.contains('is-open')) return;
    if (!d.contains(e.target)) {
      var first = d.querySelector('[data-cx-drawer-close]');
      if (first) first.focus();
    }
  });

  window.CXCart = {
    open: openDrawer,
    close: closeDrawer,
    refresh: refresh,
    add: addVariant
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bind(document); });
  } else {
    bind(document);
  }
  document.addEventListener('shopify:section:load', function (e) { bind(e.target); });
})();
